import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPurchaseConversion, type PurchaseConversion } from './billing-client';
import { trackPurchaseConfirmed } from './tracking';

/**
 * The BROWSER leg of the confirmed-purchase conversion (Meta Pixel `Subscribe`
 * / `Purchase`), shared by the two places a buyer can be right after paying:
 *
 *   - `/billing/return`, where BOTH checkouts land (the hosted Payme/Click/OCTO
 *     page redirects there; the card modal navigates there after `onSaved`);
 *   - the card modal itself, the second before it hands over to that page.
 *
 * Everything is decided server-side and echoed verbatim (GET /billing/conversion):
 * the event NAME (annual → Purchase, monthly → Subscribe), the dedup ID
 * (`subscribe-<orderId>` / `purchase-<orderId>` — the same id the server's CAPI
 * twin carries), and the amount actually CHARGED. Recomputing any of them in the
 * browser is what would make Meta count one purchase as two.
 *
 * REPEAT SENDS are harmless by construction, not by a local guard: every send
 * carries the server's event id, and the 6h echo window on `/billing/conversion`
 * sits well inside Meta's ~48h deduplication buffer, so a modal send followed by
 * the return-page send — or a refresh of the return page — collapses into ONE
 * purchase. That is a property of the endpoint, and it is tested there.
 *
 * Measured 2026-09-18 (nginx, 11–18.09): 15 of 15 FIRST card purchases did reach
 * `/billing/return` and called the endpoint within a minute; the five card
 * orders that never did were automatic renewals charged by the scheduler, where
 * no browser exists at all. The modal-side send therefore adds ~1 s of lead and
 * covers a tab closed during the post-charge spinner — nothing more.
 */

export const PURCHASE_CONVERSION_QUERY_KEY = ['billing', 'conversion'] as const;

/**
 * Send the browser leg for a server-confirmed purchase. `false` when there was
 * nothing to report (no fresh order, internal account, outside the echo window).
 *
 * ⚠️ There is no identity to hand over here, and there was never a way to.
 * Between this function and the pixel there used to be a `user` argument that
 * `trackPurchaseConfirmed` applied as Advanced Matching via
 * `fbq('set','userData', …)`; measured on production 2026-09-26, fbevents.js
 * rejects that call and the pixel's stored `userData` never changed, so the
 * whole chain was removed. The browser leg carries `external_id`/`_fbp`/`_fbc`;
 * `em`/`ph`/`fn`/`ln` travel on the server (CAPI) leg only.
 */
export function reportPurchaseConversion(
  conversion: PurchaseConversion | null | undefined,
): boolean {
  if (!conversion) return false;
  trackPurchaseConfirmed(conversion);
  return true;
}

/**
 * Declarative form, for a page: fetch once while `enabled`, fire once when the
 * server says there is a fresh purchase. `isPending` stays true until the
 * fetch settles (or forever while disabled), which the return page uses to
 * hold its "came back unpaid" verdict.
 */
export function usePurchaseConversion(enabled: boolean): {
  conversion: PurchaseConversion | null | undefined;
  isPending: boolean;
} {
  const { data: conversion, isPending } = useQuery({
    queryKey: PURCHASE_CONVERSION_QUERY_KEY,
    queryFn: fetchPurchaseConversion,
    enabled,
    // One shot. A refetch cannot discover a second purchase on this page, and
    // every extra call is another chance to fire the pixel twice.
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

  // The query result is referentially stable (`staleTime: Infinity`), so this
  // runs once per mount that saw a conversion.
  useEffect(() => {
    reportPurchaseConversion(conversion);
  }, [conversion]);

  return { conversion, isPending };
}

/**
 * Imperative form, for the card modal: fetch and fire NOW, bounded, never
 * throwing. Called between the successful charge and `onSaved()`, because the
 * parent's `onSaved` navigates away and unmounts the modal — a hook inside it
 * would race the unmount. `timeoutMs` caps how long the modal's spinner may
 * wait for the read; on timeout or error the return page sends it instead.
 * Resolves `true` when a conversion was sent.
 */
export async function sendPurchaseConversionNow(timeoutMs = 2000): Promise<boolean> {
  let timer: number | undefined;
  try {
    const conversion = await Promise.race([
      fetchPurchaseConversion(),
      new Promise<null>((resolve) => {
        timer = window.setTimeout(() => resolve(null), timeoutMs);
      }),
    ]);
    return reportPurchaseConversion(conversion);
  } catch {
    return false;
  } finally {
    if (timer !== undefined) window.clearTimeout(timer);
  }
}
